/**
 * hero-split.js — two product cards that cycle independently.
 *
 * Each <hero-split-card> runs its own timer, and the second is seeded with a
 * half-interval offset so the two never flip on the same tick. Cycling pauses
 * on hover/focus (so a customer reaching for a card doesn't have it change
 * under the cursor) and is disabled entirely under prefers-reduced-motion.
 */
class HeroSplitCard extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.hero-split__slide'));
    if (this.slides.length < 2) return;

    this.index = 0;
    this.paused = false;

    const parent = this.closest('hero-split');
    this.interval = parseInt(parent?.dataset.cycle || '5000', 10);
    this.offset = parseInt(this.dataset.offset || '0', 10);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Only animate while on screen — no timers burning in a background tab.
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? this.start() : this.stop()));
    }, { threshold: 0.2 });
    this.observer.observe(this);

    this.addEventListener('mouseenter', () => (this.paused = true));
    this.addEventListener('mouseleave', () => (this.paused = false));
    this.addEventListener('focusin', () => (this.paused = true));
    this.addEventListener('focusout', () => (this.paused = false));
  }

  start() {
    if (this.timer) return;
    this.startTimer = setTimeout(() => {
      this.timer = setInterval(() => {
        if (!this.paused) this.next();
      }, this.interval);
    }, this.offset);
  }

  stop() {
    clearTimeout(this.startTimer);
    clearInterval(this.timer);
    this.timer = null;
  }

  next() {
    const prev = this.slides[this.index];
    this.index = (this.index + 1) % this.slides.length;
    const cur = this.slides[this.index];

    prev.classList.remove('is-active');
    prev.setAttribute('aria-hidden', 'true');
    prev.setAttribute('tabindex', '-1');

    cur.classList.add('is-active');
    cur.removeAttribute('aria-hidden');
    cur.removeAttribute('tabindex');
  }

  disconnectedCallback() {
    this.stop();
    this.observer?.disconnect();
  }
}

if (!customElements.get('hero-split-card')) {
  customElements.define('hero-split-card', HeroSplitCard);
}

if (!customElements.get('hero-split')) {
  customElements.define('hero-split', class extends HTMLElement {});
}
